import { Component, createMemo, For, Show } from 'solid-js';
import { Icon, Spinner, WithLabel } from '@tcweb/connect-ui';
import { UseQueryResult } from '@tanstack/solid-query';
import {
  FileDetails,
  ListallCommentsResponse,
  ListTagsinaprojectResponse,
  UserDetailsDto,
} from '@data-access/connect/tc-tcps';
import { t } from '@trimble/i18n';
import { formatBytes } from '@trimble/utilities';
import { getFormattedDate } from '@trimble/date-utils';
import { Tags } from '../tags/Tags';
import { UserComment, UserComments } from '../comments/UserComments';

export interface ModelDetailsProps {
  projectExplorerUrl: string;
  fileDetailsQuery?: UseQueryResult<FileDetails, Error>;
  commentsQuery?: UseQueryResult<ListallCommentsResponse, Error>;
  tagsQuery?: UseQueryResult<ListTagsinaprojectResponse, Error>;
  onViewVersions(): void;
  onViewPosition(): void;
}

export const ModelDetails: Component<ModelDetailsProps> = (props) => {
  const fileDetails = createMemo((): FileDetails | undefined => {
    const query = props.fileDetailsQuery;
    if (!query || !query.isSuccess) return;
    return query.data;
  });

  const comments = createMemo((): UserComment[] => {
    const query = props.commentsQuery;
    if (!query || !query.isSuccess) return [];

    return query.data.map((c) => ({
      ...c,
      createdBy: `${c.createdBy.firstName} ${c.createdBy.lastName}`,
      modifiedBy: `${c.modifiedBy.firstName} ${c.modifiedBy.lastName}`,
    }));
  });

  const tags = createMemo((): string[] => {
    const query = props.tagsQuery;
    if (!query || !query.isSuccess) return [];

    return query.data.map((t) => t.label);
  });

  function getPathUrl(pathId: string): string {
    const projectUrl = props.projectExplorerUrl;
    const delimiter = projectUrl.endsWith('/') ? '' : '/';
    return `${projectUrl}${delimiter}${pathId}`;
  }

  function getAuditInfo(date: string, user?: UserDetailsDto): string {
    const firstName = user?.firstName ?? '';
    const lastName = user?.lastName ?? '';
    return getFormattedDate(date, firstName, lastName);
  }

  return (
    <div data-testid="model-details" class="contents">
      <Show
        when={fileDetails()}
        keyed
        fallback={
          <div data-testid="model-details-spinner" class="w-full h-[300px]">
            <Spinner show={true} />
          </div>
        }
      >
        {(fileDetails) => (
          <>
            <div class="flex flex-col p-3">
              <div data-testid="model-details-title" class="h5">
                {t('shared.ui.modelDetails.title')}
              </div>

              <Show when={fileDetails.createdOn} keyed>
                {(createdOn) => (
                  <WithLabel
                    label={t('shared.ui.modelDetails.createdOn')}
                    name=""
                  >
                    {getAuditInfo(createdOn, fileDetails.createdBy)}
                  </WithLabel>
                )}
              </Show>

              <Show when={fileDetails.modifiedOn} keyed>
                {(modifiedOn) => (
                  <WithLabel
                    label={t('shared.ui.modelDetails.modifiedOn')}
                    name=""
                  >
                    {getAuditInfo(modifiedOn, fileDetails.modifiedBy)}
                  </WithLabel>
                )}
              </Show>

              <WithLabel label={t('shared.ui.modelDetails.version')} name="">
                {fileDetails.revision ?? ''}{' '}
                {
                  <a
                    data-testid="view-versions-link"
                    class="cursor-pointer !text-blue-light"
                    onClick={props.onViewVersions}
                  >
                    {t('shared.ui.modelDetails.viewVersions')}
                  </a>
                }
              </WithLabel>

              <WithLabel label={t('shared.ui.modelDetails.size')} name="">
                {formatBytes(fileDetails.size ?? 0)}
              </WithLabel>

              <Show when={fileDetails.path} keyed>
                {(path) => (
                  <WithLabel
                    label={t('shared.ui.modelDetails.folderPath')}
                    name=""
                  >
                    <div class="flex flex-wrap items-center">
                      <For each={path}>
                        {(p, i) => (
                          <>
                            <a
                              class="!text-blue-light"
                              href={getPathUrl(p.id)}
                              target="_blank"
                            >
                              {p.name}
                            </a>
                            <Show when={i() < path.length - 1}>
                              <span class="text-xs px-2 leading-none">
                                {'>'}
                              </span>
                            </Show>
                          </>
                        )}
                      </For>
                    </div>
                  </WithLabel>
                )}
              </Show>
            </div>

            <Show when={tags().length > 0}>
              <div class="border-t border-trimble-gray-300">
                <Tags tags={tags()} />
              </div>
            </Show>

            <div
              data-testid="view-position-button"
              class="flex items-center border-t border-trimble-gray-300 cursor-pointer hover:bg-gray-light"
              onClick={props.onViewPosition}
            >
              <div class="flex flex-1 flex-col p-3">
                <div class="h5">{t('shared.ui.modelPosition.title')}</div>
                <div class="text-sm text-gray-6">
                  {t('shared.ui.modelPosition.description')}
                </div>
              </div>
              <div class="pr-2">
                <Icon icon="tc-icon-chevron-right" />
              </div>
            </div>

            <Show when={comments().length > 0}>
              <div class="border-t border-trimble-gray-300">
                <UserComments comments={comments()} />
              </div>
            </Show>
          </>
        )}
      </Show>
    </div>
  );
};
